import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import Select from "react-select";
import {
  getErpAuthState,
  isErpApiSuccess,
  productDeliveryRepairingOrder,
  requestErpOtp,
  verifyErpOtp,
} from "../erpClient";
import { createRepairingOrderIdempotencyKey } from "../utils/repairingOrdersErpAdapter";
import {
  getMobileValidationError,
  sanitizeMobileInput,
} from "../utils/mobileInput";
import { formatIndianRupee } from "../utils/formatLiveRate";

const OTP_LENGTH = 6;

const PAYMENT_METHOD_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
];

const PAYMENT_MODE_API = {
  cash: "CASH",
  cheque: "CHEQUE",
  bank_transfer: "BANK_TRANSFER",
  upi: "UPI",
  card: "CARD",
};

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({
    ...base,
    minHeight: "31px",
    height: "31px",
  }),
};

const resolveTenantId = () => {
  const user = getErpAuthState()?.user || {};
  const n = Number(user.tenant_id ?? user.tenantId);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

const ProductReceivedModal = ({
  show,
  onHide,
  order,
  eligibleItems = [],
  bankAccountsList = [],
  buttonClass = "",
  cardClass = "",
  onSuccess,
}) => {
  const [step, setStep] = useState("otp");
  const [otpPhase, setOtpPhase] = useState("send");
  const [otp, setOtp] = useState("");
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [amountReceived, setAmountReceived] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [payMode, setPayMode] = useState("cash");
  const [bankAccountId, setBankAccountId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const idempotencyKeyRef = useRef(null);
  const otpInputRefs = useRef([]);

  const customerMobile = useMemo(() => {
    const raw =
      order?.mobile ||
      order?.customer_mobile ||
      order?.user_mobile ||
      "";
    return sanitizeMobileInput(raw);
  }, [order]);

  const customerName =
    order?.name || order?.customer_name || order?.user_name || "Customer";

  const displayPhone =
    customerMobile.length === 10
      ? `+91 ${customerMobile.slice(0, 5)} ${customerMobile.slice(5)}`
      : customerMobile
        ? `+91 ${customerMobile}`
        : "—";

  const bankOptions = useMemo(
    () =>
      bankAccountsList.map((bank) => ({
        value: String(bank.id),
        label: bank.bank_name || bank.account_number || `Bank ${bank.id}`,
      })),
    [bankAccountsList],
  );

  const itemOptions = useMemo(
    () =>
      eligibleItems.map((item) => ({
        value: String(item.id),
        label:
          item.product_group_name ||
          item.product_type ||
          item.tag_no ||
          `Item #${item.id}`,
      })),
    [eligibleItems],
  );

  const clearOtp = () => {
    setOtp("");
    otpInputRefs.current[0]?.focus();
  };

  const resetState = () => {
    setStep("otp");
    setOtpPhase("send");
    setOtp("");
    setIsOtpLoading(false);
    setSelectedItemId("");
    setAmountReceived("");
    setDeliveryDate(new Date().toISOString().split("T")[0]);
    setPayMode("cash");
    setBankAccountId("");
    setRemarks("");
    setIsSaving(false);
    idempotencyKeyRef.current = null;
  };

  useEffect(() => {
    if (!show) {
      resetState();
      return;
    }
    if (eligibleItems.length === 1) {
      setSelectedItemId(String(eligibleItems[0].id));
    }
  }, [show, eligibleItems]);

  useEffect(() => {
    if (!show || payMode !== "cash" || bankOptions.length === 0) return;
    const cashBank = bankAccountsList.find((bank) => {
      const type = String(bank.account_type || "").toLowerCase();
      const name = String(bank.bank_name || "").toLowerCase();
      return type === "cash" || name.includes("cash");
    });
    if (cashBank?.id != null) {
      setBankAccountId(String(cashBank.id));
    } else if (!bankAccountId && bankOptions[0]) {
      setBankAccountId(bankOptions[0].value);
    }
  }, [show, payMode, bankAccountsList, bankOptions, bankAccountId]);

  useEffect(() => {
    if (show && step === "otp" && otpPhase === "verify") {
      const timer = window.setTimeout(
        () => otpInputRefs.current[0]?.focus(),
        120,
      );
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [show, step, otpPhase]);

  const handleClose = () => {
    if (isSaving || isOtpLoading) return;
    onHide();
  };

  const getOtpDigit = (index) => otp[index] ?? "";

  const focusOtpInput = (index) => {
    if (index >= 0 && index < OTP_LENGTH) {
      otpInputRefs.current[index]?.focus();
    }
  };

  const handleOtpDigitChange = (index, e) => {
    const value = e.target.value.replace(/\D/g, "").slice(-1);
    const chars = otp.split("");
    while (chars.length < OTP_LENGTH) {
      chars.push("");
    }
    chars[index] = value;
    const nextOtp = chars.join("").slice(0, OTP_LENGTH);
    setOtp(nextOtp);
    if (value && index < OTP_LENGTH - 1) {
      focusOtpInput(index + 1);
    }
  };

  const handleOtpDigitKeyDown = (index, e) => {
    if (e.key === "Backspace" && !getOtpDigit(index) && index > 0) {
      focusOtpInput(index - 1);
    }
    if (e.key === "ArrowLeft" && index > 0) {
      focusOtpInput(index - 1);
    }
    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      focusOtpInput(index + 1);
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    setOtp(pasted);
    focusOtpInput(Math.min(pasted.length, OTP_LENGTH - 1));
  };

  const handleSendOtp = async () => {
    const mobileError = getMobileValidationError(customerMobile, {
      required: true,
    });
    if (mobileError) {
      toast.error(mobileError);
      return;
    }

    setIsOtpLoading(true);
    try {
      const result = await requestErpOtp({
        tenantId: resolveTenantId(),
        phone: customerMobile,
        type: "repairing_order",
      });
      if (isErpApiSuccess(result)) {
        toast.success(result?.message || "OTP sent successfully");
        setOtpPhase("verify");
        clearOtp();
      } else {
        toast.error(result?.message || "Failed to send OTP");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Failed to send OTP",
      );
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== OTP_LENGTH) {
      toast.error("Please enter complete OTP");
      return;
    }

    setIsOtpLoading(true);
    try {
      const result = await verifyErpOtp({
        tenantId: resolveTenantId(),
        phone: customerMobile,
        otp,
        persistSession: false,
      });
      if (isErpApiSuccess(result)) {
        toast.success(result?.message || "OTP verified successfully");
        setStep("settlement");
      } else {
        toast.error(result?.message || "Invalid OTP");
        clearOtp();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to verify OTP",
      );
      clearOtp();
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleOtpAction = async () => {
    if (otpPhase === "send") {
      await handleSendOtp();
    } else {
      await handleVerifyOtp();
    }
  };

  const handleSave = async () => {
    if (!selectedItemId) {
      toast.error("Select a product item");
      return;
    }

    const amount = parseFloat(amountReceived) || 0;
    if (amount < 0) {
      toast.error("Enter a valid amount");
      return;
    }

    if (amount > 0 && !bankAccountId) {
      toast.error("Select a bank account for payment");
      return;
    }

    if (amount > 0 && !payMode) {
      toast.error("Select a payment mode");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        repairing_order_item_id: Number(selectedItemId),
        amount_received: amount,
        product_delivery_date: deliveryDate,
        bank_account_id: Number(bankAccountId) || undefined,
        payment_mode: PAYMENT_MODE_API[payMode] || String(payMode).toUpperCase(),
      };

      if (amount > 0) {
        if (!idempotencyKeyRef.current) {
          idempotencyKeyRef.current = createRepairingOrderIdempotencyKey();
        }
        payload.idempotency_key = idempotencyKeyRef.current;
      }

      const response = await productDeliveryRepairingOrder(payload);
      if (isErpApiSuccess(response)) {
        idempotencyKeyRef.current = null;
        toast.success("Product received successfully");
        onSuccess?.();
        onHide();
      } else {
        toast.error(response?.message || "Failed to save product delivery");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save product delivery",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const selectedItem = eligibleItems.find(
    (item) => String(item.id) === String(selectedItemId),
  );

  const otpActionDisabled =
    otpPhase === "send"
      ? isOtpLoading || customerMobile.length !== 10
      : isOtpLoading || otp.length !== OTP_LENGTH;

  const otpActionLabel =
    otpPhase === "send"
      ? isOtpLoading
        ? "Sending..."
        : "Send OTP"
      : isOtpLoading
        ? "Verifying..."
        : "Verify OTP";

  const borderCardClass = `border ${buttonClass || cardClass}`.trim();

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size={step === "settlement" ? "xl" : "md"}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Product Received</Modal.Title>
      </Modal.Header>

      {step === "otp" ? (
        <>
          <Modal.Body className="pt-3 pb-1 px-4">
            <div className={`${borderCardClass} bg-white p-3 mb-3`}>
              <div className="d-flex align-items-start gap-3">
                <div
                  className={`d-flex align-items-center justify-content-center flex-shrink-0 bg-primary-subtle text-primary ${buttonClass}`}
                  style={{ width: 42, height: 42 }}
                >
                  <Icon icon="solar:shield-check-bold" width={22} height={22} />
                </div>
                <div className="flex-grow-1 min-w-0">
                  <div className="text-uppercase small fw-semibold text-muted">
                    Customer Verification
                  </div>
                  <div className="fw-semibold text-dark mt-1">{customerName}</div>
                  <p className="small text-muted mb-0 mt-2">
                    {otpPhase === "send" ? (
                      <>
                        For security purpose we will send OTP to the{" "}
                        <strong className="text-dark">{customerMobile || "—"}</strong>{" "}
                        number.
                      </>
                    ) : (
                      <>
                        We sent a 6-digit code to{" "}
                        <strong className="text-dark">{displayPhone}</strong>.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {otpPhase === "verify" && (
              <>
                <Form.Group className="mb-2">
                  <Form.Label className="fw-semibold mb-2 text-uppercase small text-dark">
                    Security Code
                  </Form.Label>
                  <div
                    className="d-flex justify-content-center gap-2 flex-wrap"
                    onPaste={handleOtpPaste}
                  >
                    {Array.from({ length: OTP_LENGTH }, (_, index) => (
                      <Form.Control
                        key={index}
                        ref={(el) => {
                          otpInputRefs.current[index] = el;
                        }}
                        id={index === 0 ? "product-received-otp-0" : undefined}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        size="sm"
                        value={getOtpDigit(index)}
                        onChange={(e) => handleOtpDigitChange(index, e)}
                        onKeyDown={(e) => handleOtpDigitKeyDown(index, e)}
                        className={`text-center shadow-none fw-semibold ${buttonClass}`}
                        style={{ width: 44, height: 44, fontSize: "1.05rem" }}
                        autoComplete={index === 0 ? "one-time-code" : "off"}
                        aria-label={`Digit ${index + 1}`}
                      />
                    ))}
                  </div>
                </Form.Group>
                <p className="text-center small text-muted mb-0">
                  Didn&apos;t receive a code?{" "}
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 align-baseline"
                    onClick={handleSendOtp}
                    disabled={isOtpLoading}
                  >
                    Resend
                  </button>
                </p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              className={buttonClass}
              onClick={handleOtpAction}
              disabled={otpActionDisabled}
            >
              {otpActionLabel}
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <>
          <Modal.Body className="pt-3 pb-1 px-4">
            <div className={`${borderCardClass} bg-success-subtle p-3 mb-3`}>
              <div className="small fw-semibold text-success">
                OTP verified for {displayPhone}
              </div>
              <div className="small text-muted mt-1">
                Complete payment settlement to hand over the product to{" "}
                {customerName}.
              </div>
            </div>

            {itemOptions.length > 1 && (
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold mb-1 text-uppercase small">
                  Select Item
                </Form.Label>
                <Select
                  options={itemOptions}
                  value={
                    itemOptions.find((opt) => opt.value === selectedItemId) ||
                    null
                  }
                  onChange={(option) =>
                    setSelectedItemId(option ? option.value : "")
                  }
                  placeholder="Select product item"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </Form.Group>
            )}

            {selectedItem && (
              <div className={`${borderCardClass} bg-white p-3 mb-3`}>
                <div className="text-uppercase small fw-semibold text-muted">
                  Product
                </div>
                <div className="fw-semibold mt-1">
                  {selectedItem.product_group_name ||
                    selectedItem.product_type ||
                    "Repair item"}
                </div>
              </div>
            )}

            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-1 text-uppercase small">
                    Amount Received
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    step="0.01"
                    className={`shadow-none ${buttonClass}`}
                    placeholder="0.00"
                    value={amountReceived}
                    onChange={(e) => setAmountReceived(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-1 text-uppercase small">
                    Delivery Date
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    className={`shadow-none ${buttonClass}`}
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <h6 className="fw-semibold mb-2 text-uppercase small text-dark">
              Payment Settlement
            </h6>
            <div className={`table-responsive border mb-0 ${buttonClass}`}>
              <table className="table table-sm align-middle mb-0">
                <thead className="bg-primary-subtle small text-uppercase">
                  <tr>
                    <th>Mode</th>
                    <th style={{ width: "220px" }}>Type</th>
                    <th>Remarks</th>
                    <th className="text-end" style={{ width: "140px" }}>
                      Amt
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <Select
                        options={PAYMENT_METHOD_OPTIONS}
                        value={
                          PAYMENT_METHOD_OPTIONS.find(
                            (opt) => opt.value === payMode,
                          ) || null
                        }
                        onChange={(option) =>
                          setPayMode(option ? option.value : "")
                        }
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                      />
                    </td>
                    <td>
                      <Select
                        options={bankOptions}
                        value={
                          bankOptions.find((opt) => opt.value === bankAccountId) ||
                          null
                        }
                        onChange={(option) =>
                          setBankAccountId(option ? option.value : "")
                        }
                        placeholder="Select Type"
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        className={`shadow-none ${buttonClass}`}
                        type="text"
                        placeholder="Remarks"
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                      />
                    </td>
                    <td className="text-end fw-semibold">
                      {formatIndianRupee(parseFloat(amountReceived) || 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              className={buttonClass}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default ProductReceivedModal;
